import type { ElectronApi } from '../shared/electron-api';
import {
  ControllerSession,
  type RemotePeerInfo,
} from '../renderer/controller';
import { MobileApi } from './api';
import type { PointerInputBinding } from './pointer';
import type { KeyboardInputBinding } from './keyboard';
import { platformLabel, type PlatformKind } from '../shared/platform';
import type { ShortcutMode } from '../shared/key-compat';
import {
  DEFAULT_REMOTE_STREAM_SETTINGS,
  normalizeRemoteStreamSettings,
  type RemoteStreamSettings,
} from '../shared/stream-profile';
import type { FileTransferProgress, FileTransferRateState } from '../shared/filexfer';
import type { SessionAccessMode } from '../shared/session-mode';

// 移动端入口共享状态容器。拆分 app.ts 后，所有跨模块读写的模块级变量统一
// 收敛到此处，避免 ES Module 导入绑定不可重新赋值（read-only）的限制。
// 运行期行为与原单体模块逐字一致。
export const appState = {
  controller: null as ControllerSession | null,
  video: undefined as HTMLVideoElement | undefined,
  pointerInput: null as PointerInputBinding | null,
  keyboardInput: null as KeyboardInputBinding | null,
  remotePlatform: 'unknown' as PlatformKind,
  shortcutMode: 'auto' as ShortcutMode,
  authBusy: false,
  devicesRefreshTimer: null as number | null,
  devicesLoading: false,
  connectionPreflightCanConnect: true,
  accountValidationTimer: null as number | null,
  accountValidationBusy: false,
  pendingRemoteClipboard: null as string | null,
  remoteClipboardRevision: 0,
  clipboardWriteBusy: false,
  fileTransferBusy: false,
  fileDragDepth: 0,
  fileTransferRenderTimer: null as number | null,
  fileTransferPanelOpen: false,
  sessionControlsHidden: false,
  sessionStatsOverlay: false,
  sessionWasConnected: false,
  sessionStalled: false,
  remoteStreamSettings: { ...DEFAULT_REMOTE_STREAM_SETTINGS } as RemoteStreamSettings,
  sessionMode: 'control' as SessionAccessMode,
  retrySession: null as (() => void) | null,
  cancelSession: null as (() => void) | null,
  streamSettingsTrigger: null as HTMLElement | null,
  streamSettingsTimer: null as number | null,
  rightMode: false,
  dragMode: false,
};

// 锁存的修饰键（Ctrl/Alt/Shift/Meta）。以对象形式导出，模块内就地变更。
export const mods = new Set<string>();
// 文件传输进度表。
export const fileTransfers = new Map<string, WebFileTransferEntry>();

export const api = new MobileApi();
export const MAX_WEB_FILE_COUNT = 32;
export const MAX_WEB_FILE_BYTES = 1024 * 1024 * 1024;
export const SESSION_KEY = 'rc_web_session';
export const THEME_KEY = 'rc_web_theme';
export const HIST_KEY = 'rc_lan_history';
export const STATS_OVERLAY_KEY = 'rc_web_session_stats_overlay';
export const REMOTE_STREAM_SETTINGS_KEY = 'rc_web_remote_stream_settings';

type WebTheme = 'light' | 'dark';
interface SavedSession {
  token: string;
  refreshToken?: string;
  username: string;
}
interface WebFileTransferEntry extends FileTransferProgress, FileTransferRateState {
  updatedAt: number;
  speedBps: number;
}
interface LanEntry { addr: string; pass: string; ts: number; }

export type { WebTheme, SavedSession, WebFileTransferEntry, LanEntry };

const $ = <T extends HTMLElement = HTMLElement>(id: string): T =>
  document.getElementById(id) as T;
export { $ };

function systemUsesDarkIcons(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredTheme(): WebTheme | null {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
}

function readTheme(): WebTheme {
  return readStoredTheme() || (systemUsesDarkIcons() ? 'dark' : 'light');
}

function readStoredStatsOverlay(): boolean {
  try {
    // 普通用户默认不需要看到工程指标；需要排查连接质量时，可在画面设置中开启。
    return localStorage.getItem(STATS_OVERLAY_KEY) === 'on';
  } catch {
    return false;
  }
}

function saveStatsOverlay(enabled: boolean): void {
  try {
    localStorage.setItem(STATS_OVERLAY_KEY, enabled ? 'on' : 'off');
  } catch {
    // 隐私模式或浏览器禁用存储时，当前会话中的设置仍然有效。
  }
}

function readStoredRemoteStreamSettings(): RemoteStreamSettings {
  try {
    const raw = localStorage.getItem(REMOTE_STREAM_SETTINGS_KEY);
    return normalizeRemoteStreamSettings(raw ? JSON.parse(raw) : DEFAULT_REMOTE_STREAM_SETTINGS);
  } catch {
    return { ...DEFAULT_REMOTE_STREAM_SETTINGS };
  }
}

function saveRemoteStreamSettings(settings: RemoteStreamSettings): void {
  try {
    localStorage.setItem(REMOTE_STREAM_SETTINGS_KEY, JSON.stringify(normalizeRemoteStreamSettings(settings)));
  } catch {
    // 隐私模式或浏览器禁用存储时，当前会话中的设置仍然有效。
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const m: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return m[c] || c;
  });
}

function friendlyPlatform(value?: string): { icon: string; label: string } {
  const platform = String(value || '').toLowerCase();
  if (platform.includes('darwin') || platform.includes('mac')) return { icon: 'icon-mac', label: 'Mac' };
  if (platform.includes('win')) return { icon: 'icon-windows', label: 'Windows' };
  if (platform.includes('linux')) return { icon: 'icon-linux', label: 'Linux' };
  return { icon: 'icon-device', label: '电脑' };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fileTransferPercent(progress: FileTransferProgress): number {
  if (progress.size <= 0) return progress.state === 'transferring' ? 0 : 100;
  return Math.min(100, Math.max(0, (progress.transferred / progress.size) * 100));
}

function fileTransferStateLabel(progress: FileTransferProgress): string {
  if (progress.state === 'completed') return progress.direction === 'send' ? '已发送' : '已接收';
  if (progress.state === 'failed') return progress.error || '传输失败';
  if (progress.state === 'cancelled') return '已取消';
  return progress.direction === 'send' ? '发送中' : '接收中';
}

function formatTransferRate(bytesPerSecond: number): string {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond < 1) return '测速中…';
  return `${formatFileSize(bytesPerSecond)}/s`;
}

function formatTransferEta(progress: FileTransferProgress, speedBps: number): string {
  if (progress.state !== 'transferring' || progress.size <= 0) return '';
  const remaining = Math.max(0, progress.size - progress.transferred);
  if (remaining === 0) return '正在保存并校验…';
  if (!Number.isFinite(speedBps) || speedBps < 1) return '计算中…';
  const seconds = Math.max(1, Math.ceil(remaining / speedBps));
  if (seconds < 60) return `${seconds} 秒`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} 分钟`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);
  return minutes > 0 ? `${hours} 小时 ${minutes} 分钟` : `${hours} 小时`;
}

function streamQualityLabel(value: RemoteStreamSettings['quality']): string {
  if (value === '720p') return '省流量';
  if (value === '1080p') return '清晰';
  if (value === '1440p') return '更清晰';
  if (value === '2160p') return '极清晰';
  return '自动';
}

function streamFrameRateLabel(value: RemoteStreamSettings['frameRate']): string {
  if (value === 24) return '省电';
  if (value === 30) return '标准';
  if (value === 60) return '更流畅';
  return '自动';
}

export {
  readStoredTheme,
  systemUsesDarkIcons,
  readTheme,
  readStoredStatsOverlay,
  saveStatsOverlay,
  readStoredRemoteStreamSettings,
  saveRemoteStreamSettings,
  escapeHtml,
  friendlyPlatform,
  formatFileSize,
  fileTransferPercent,
  fileTransferStateLabel,
  formatTransferRate,
  formatTransferEta,
  streamQualityLabel,
  streamFrameRateLabel,
  platformLabel,
  type RemotePeerInfo,
  type ElectronApi,
};
